import { Button } from '@/components/ui/button';
import { FileText, FileSpreadsheet } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useVatModeStore } from '@/stores/vatModeStore';

export function ExportButtons({ dataType, label, startDate, endDate, filters, data, filename, columns, branchIds, testid }) {
  const handleExport = async (format) => {
    try {
      toast.loading(`Generating ${format.toUpperCase()}...`);

      // If inline data is provided, export directly (legacy usage)
      if (data && columns) {
        const csvContent = [columns.join(','), ...data.map(row => columns.map(c => JSON.stringify(row[c] ?? '')).join(','))].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${filename || 'export'}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        toast.dismiss();
        toast.success('CSV downloaded');
        return;
      }

      // API-based export with date filtering
      const payload = { type: dataType, format, vat_mode: useVatModeStore.getState().inclVat ? 'incl' : 'excl' };
      // Defensive: accept Date objects, ISO strings, or YYYY-MM-DD — always
      // emit YYYY-MM-DD so the backend string-compares correctly against
      // Mongo records. Prevents the "JS locale string" regression.
      const toIso = (v) => {
        if (!v) return null;
        if (v instanceof Date) {
          const y = v.getFullYear();
          const m = String(v.getMonth() + 1).padStart(2, '0');
          const d = String(v.getDate()).padStart(2, '0');
          return `${y}-${m}-${d}`;
        }
        return String(v).slice(0, 10);
      };
      const s = toIso(startDate);
      const e = toIso(endDate);
      if (s) payload.start_date = s;
      if (e) payload.end_date = e;
      // Universal: the page's branch selection ([] = all permitted) drives the export exactly like the screen.
      const bids = Array.isArray(branchIds) ? branchIds.filter(Boolean) : null;
      const f = { ...(filters || {}) };
      if (bids && bids.length) { f.branch_ids = bids; f.branch_id = bids.length === 1 ? bids[0] : null; }
      if (Object.keys(f).length) payload.filters = f;
      const response = await api.post('/export/data', payload, { responseType: 'blob' });
      const h = response.headers || {};
      const isZip = h['x-export-zip'] === '1';
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const dateSuffix = s && e ? `_${s}_to_${e}` : s ? `_${s}` : '';
      const ext = isZip ? 'zip' : format === 'pdf' ? 'pdf' : format === 'csv' ? 'csv' : 'xlsx';
      const bsuf = bids && bids.length ? `_${bids.length}br` : '';
      link.setAttribute('download', `${dataType}_report${dateSuffix}${bsuf}.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.dismiss();
      const fmtN = (v) => Number(v).toLocaleString();
      if (isZip) {
        const msg = `Large export split into ${h['x-export-files']} files (ZIP): ${fmtN(h['x-export-rows'])} of ${fmtN(h['x-export-total'])} records`;
        if (h['x-export-complete'] === '1') toast.success(`${msg} — complete`, { duration: 10000 });
        else toast.warning(`${msg} — records changed during export, please export again`, { duration: 10000 });
      } else if (h['x-export-partial'] === '1') {
        toast.warning(`Partial export: ${Number(h['x-export-rows']).toLocaleString()} of ${Number(h['x-export-total']).toLocaleString()} records. Narrow the date range to export everything.`, { duration: 10000 });
      } else if (h['x-export-total']) {
        toast.success(`${format.toUpperCase()} downloaded — complete: ${fmtN(h['x-export-total'])} records`);
      } else {
        toast.success(`${format.toUpperCase()} downloaded`);
      }
    } catch (err) {
      toast.dismiss();
      // If the server returned a blob with a JSON error, decode it
      let detail = 'Export failed';
      try {
        if (err?.response?.data instanceof Blob) {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          if (parsed?.detail) detail = parsed.detail;
        } else if (err?.response?.data?.detail) {
          detail = err.response.data.detail;
        }
      } catch { /* keep default */ }
      toast.error(detail);
    }
  };

  return (
    <div className="flex gap-2" data-testid={testid || `export-${dataType || 'data'}`}>
      <Button size="sm" variant="outline" onClick={() => handleExport('pdf')} className="h-8 text-xs rounded-full" data-testid={`export-${dataType || 'data'}-pdf`}>
        <FileText size={14} className="mr-1" /> PDF
      </Button>
      <Button size="sm" variant="outline" onClick={() => handleExport('excel')} className="h-8 text-xs rounded-full" data-testid={`export-${dataType || 'data'}-excel`}>
        <FileSpreadsheet size={14} className="mr-1" /> Excel
      </Button>
      <Button size="sm" variant="outline" onClick={() => handleExport('csv')} className="h-8 text-xs rounded-full" data-testid={`export-${dataType || 'data'}-csv`}>
        <FileSpreadsheet size={14} className="mr-1" /> CSV
      </Button>
    </div>
  );
}
